import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UtilityService } from '@eforall/common';
import { DashboardDialogFramePart, DialogAction, DialogContext, DialogService, GridContentWidget } from '@eforall/dashboard';
import { SessionAttendeeStatus } from '@interfaces';
import { ReadonlyAccessWidget } from '../../../../../../../ui/widget';
import { AcceleratorAreaService } from '../../../../accelerator-area.service';
import { getSetup } from './grid/get-setup';

export interface AttendeeRow {
	accSessionAttendeeId?: number,
	accEntrepreneurId: number,
	personId: number,
	fullName: string,
	accVentureId: number,
	droppedOut: boolean,
	business: string,
	attended?: SessionAttendeeStatus,
	survey: boolean,
	cityAndState?: string,
	email?: string,
	phone?: string,
	zip?: number,
	firstName: string,
	lastName: string,
	updatedUTC: number,
}

@Component({
	selector: 'session-attendance-dialog',
	standalone: true,
	imports: [
		DashboardDialogFramePart,
		ReadonlyAccessWidget,
		GridContentWidget,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './attendance.dialog.html',
})
export class SessionAttendanceDialog {
	public dialog: DialogContext<{ accSessionId: number, specialistNames: string }> = inject(MAT_DIALOG_DATA);
	public dialogRef = inject(MatDialogRef<SessionAttendanceDialog>);
	public accAreaService = inject(AcceleratorAreaService);
	public util = inject(UtilityService);
	public dialogService = inject(DialogService);

	updateAction: DialogAction = {
		id: 'sendSurvey',
		cssClass: signal(undefined),
		enabled: signal(true),
		visible: signal(true),
		text: signal('Send Surveys'),
		linkType: 'callback',
		callback: this.checkIfCanSendSurvey.bind(this),
		willCloseDialog: false,
	};

	cancelAction: DialogAction = {
		id: 'close',
		cssClass: signal(undefined),
		enabled: signal(true),
		visible: signal(true),
		text: signal('Close'),
		linkType: "no-action",
		willCloseDialog: true,
	};

	actions: DialogAction[] = [this.updateAction, this.cancelAction];

	icon = signal('fa-regular fa-hand-pointer');

	header = computed(() => {
		const accSessionId = this.dialog.data?.accSessionId;

		const data = this.accAreaService.data();
		const accSession = data?.sessions.find(accSession => accSession.accSessionId == accSessionId);
		if (!accSessionId || !accSession || !data) return 'No Acc Session';

		const event = data.events.find(e => e.eventId == accSession.eventId);
		return event ? `Attendance for ${event.eventName} - ${this.util.date.formatUTC(event.startUTC, 'MMM D, YYYY (DOW)', 'H:MM AM EST', 'en-US')}` : 'No Event';
	});



	public isReadonly = computed(() => !this.accAreaService.canEdit());


	public setup = getSetup(this.util, this.dialogService, this.accAreaService, this.dialog.data?.accSessionId);

	public attendees = computed<AttendeeRow[]>(() => {
		const data = this.accAreaService.data();

		const accSessionId = this.dialog.data?.accSessionId;
		if (!data || !accSessionId) return [];

		const accSession = data.sessions.find(accSession => accSession.accSessionId == accSessionId);
		if (!accSession) return [];

		const event = data.events.find(event => event.eventId == accSession.eventId);
		if (!event) return [];

		return data.entrepreneurs
			.map(e => {
				const attendeeRecord = data.sessionAttendees.find(attendee => attendee.accSessionId == accSessionId && attendee.personId == e.personId);
				const accVenture = data.ventures.find(v => v.accVentureId == e.accVentureId)!;
				const survey = attendeeRecord ? !!data.sessionSurveyResponses.find(ssr => ssr.accSessionAttendeeId == attendeeRecord.accSessionAttendeeId) : false;

				return {
					accEntrepreneurId: e.accEntrepreneurId,
					accSessionAttendeeId: attendeeRecord?.accSessionAttendeeId,
					accVentureId: accVenture.accVentureId,
					droppedOut: accVenture.status !== 'Participating',
					attended: attendeeRecord?.status,
					survey,
					business: accVenture.ventureName,
					fullName: e.fullName,
					personId: e.personId,
					cityAndState: e.cityAndState,
					updatedUTC: e.updatedUTC,
					email: e.email,
					phone: e.phone,
					firstName: e.firstName,
					lastName: e.lastName,
					zip: e.zip,
				};

			})
			.filter(r => !!r.attended || !r.droppedOut);

	});

	private async checkIfCanSendSurvey() {
		const data = this.accAreaService.data();

		const accSessionId = this.dialog.data?.accSessionId;
		if (!data || !accSessionId) return;

		const accSession = data.sessions.find(accSession => accSession.accSessionId == accSessionId);
		if (!accSession) return;

		const event = data.events.find(event => event.eventId == accSession.eventId);
		if (!event) return;


		const utc = Date.now() / 1000;
		let toolEnabledSendSurvey = true;

		if (utc < event.toolStartUTC) {
			const timePeriod = this.util.date.calculateTimePeriod(utc, event.toolStartUTC);
			const message = `The survey tool is currently inactive, but it will be accessible to entrepreneurs in ${timePeriod}. Would you like to proceed with sending the survey email?`;
			toolEnabledSendSurvey = await this.dialogService.confirm(message, undefined, undefined, undefined, 350, 200);
		}
		else if (utc > event.toolEndUTC) {
			const timePeriod = this.util.date.calculateTimePeriod(event.toolEndUTC, utc);
			const message = `Sending the survey email is no longer possible. The survey is closed as of ${timePeriod} ago.`;
			await this.dialogService.showMessage('Survey Closed', message, undefined, undefined, 350, 190);
			toolEnabledSendSurvey = false;
		} else if (accSession.surveyEmailSent) {
			const message = `The survey email has already been sent. Nudge emails will AUTOMATICALLY be sent to entrepreneurs who have not filled out the survey every 2 days until the survey closes on ${this.util.date.formatUTC(event.toolEndUTC, 'MMM D, YYYY (DOW)', 'H:MM AM EST', 'en-US')}.`;
			await this.dialogService.showMessage('Survey Email Sent', message, 'Okay', undefined, 350, 200);
			toolEnabledSendSurvey = false;
		}


		if (toolEnabledSendSurvey) {

			const yes = this.dialog.data?.specialistNames && this.dialog.data.specialistNames != 'n/a' ? await this.dialogService.confirm(`Was ${this.dialog.data.specialistNames} the specialist?\n\nIf not, please correct it before sending the survey.`, 'Yes', 'No', undefined, 450, 170) : true;
			if (yes) {
				const atleastOneAttendanceNotMarked = !!this.attendees().find(r => !r.attended);
				const countMarkedPresentWithNoSurvey = this.attendees().filter(r => r.attended && r.attended == 'Present' && !r.survey).length;
				if (atleastOneAttendanceNotMarked) await this.dialogService.showMessage('Attendance', 'All attendance must be provided before surveys can be sent.', 'Okay', undefined, 300, 180);
				else if (countMarkedPresentWithNoSurvey) {
					const sendMsg = `An email with a link to the survey will be sent to the ${countMarkedPresentWithNoSurvey == 1 ? '1 Entrepreneur' : `${countMarkedPresentWithNoSurvey} Entrepreneurs`} that have been marked present, but haven't submitted it yet.\n\n This survey will close on ${this.util.date.formatUTC(event.toolEndUTC, 'MMM D, YYYY (DOW)', 'H:MM AM EST', 'en-US')}`;

					const send = await this.dialogService.confirm(sendMsg, 'Send it', 'Cancel', undefined, 400, 280);

					if (send) {
						const emailSent = await this.accAreaService.sendAttendanceEmail(accSessionId);

						await this.dialogService.showMessage('Survey Sent', `Survey email has been sent.`, 'Okay', undefined, 250, 120);
						this.dialogRef.close(this.updateAction);

					}
				}
				else await this.dialogService.showMessage('All Set', 'All entrepreneurs have already filled out the survey.', 'Okay', undefined, 450, 150);

			}
		}
	}


}
